import { useRef, useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { Download, Upload } from "lucide-react";
import MainSectionSection from "../../components/MainSectionSection";
import SectionTitle from "../../components/SectionTitle";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { trpc } from "@/trpc";

const EXPECTED_COLUMNS = ["FINCA", "METRO", "MLS", "WASI", "CIENCUADRAS", "MERCADO LIBRE"];

// Mirrors the header validation of the MLS enrichment reader (delimiter = "," or ";",
// whichever appears more in the header; first columns must match EXPECTED_COLUMNS in order).
const validateCsvHeader = async (file: File): Promise<string | null> => {
  const head = await file.slice(0, 4096).text();
  const header = head.split("\n")[0] ?? "";
  const commaCount = (header.match(/,/g) || []).length;
  const semicolonCount = (header.match(/;/g) || []).length;
  const delimiter = semicolonCount > commaCount ? ";" : ",";
  const columns = header.split(delimiter).map((col) => col.trim());

  for (let i = 0; i < EXPECTED_COLUMNS.length; i++) {
    if (columns[i] !== EXPECTED_COLUMNS[i]) {
      return `El archivo no tiene el formato esperado. Las primeras columnas deben ser: ${EXPECTED_COLUMNS.join(
        ", "
      )}.`;
    }
  }

  return null;
};

const PortalCodesContainer = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const trpcUtils = trpc.useUtils();
  const fileStatusQuery = trpc.inventory.portalCodesFileStatus.useQuery();
  const getUploadUrlMutation = trpc.inventory.getPortalCodesUploadUrl.useMutation();

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again re-triggers onChange
    event.target.value = "";
    if (!file) return;

    const validationError = await validateCsvHeader(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsUploading(true);
    try {
      const { uploadUrl } = await getUploadUrlMutation.mutateAsync();

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": "text/csv",
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file to S3");
      }

      toast.success("Archivo subido correctamente.");
      await trpcUtils.inventory.portalCodesFileStatus.invalidate();
    } catch (error) {
      console.error("Error uploading portal codes file:", error);
      toast.error("No se pudo subir el archivo. Inténtalo de nuevo.");
    } finally {
      setIsUploading(false);
    }
  };

  const status = fileStatusQuery.data;

  return (
    <MainSectionSection>
      <SectionTitle title="Códigos de Portales" />
      <p className="text-gray-600">
        Sube el archivo CSV con los códigos de portales que se utiliza para el enriquecimiento del
        inventario. Al subir un nuevo archivo se reemplaza el archivo actual.
      </p>

      <div className="flex max-w-2xl flex-col gap-4 rounded-lg border border-gray-primary bg-white p-4 md:p-6">
        <div className="text-sm font-semibold text-gray-700">Archivo actual</div>

        {fileStatusQuery.isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Spinner />
          </div>
        ) : fileStatusQuery.isError ? (
          <p className="text-sm text-red-600">No se pudo cargar el estado del archivo.</p>
        ) : status?.exists ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-gray-600">
              Última actualización:{" "}
              <span className="font-medium text-gray-800">
                {status.lastModified
                  ? dayjs(status.lastModified).format("DD/MM/YYYY h:mm A")
                  : "—"}
              </span>
            </p>
            {status.downloadUrl && (
              <a
                href={status.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-palomma-primary hover:underline"
              >
                <Download className="size-4" />
                Descargar archivo actual
              </a>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-600">Aún no se ha subido ningún archivo.</p>
        )}

        <div className="border-t border-gray-primary pt-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileSelected}
          />
          <Button
            className="min-h-11 w-full md:w-auto"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? (
              <>
                <Spinner size="sm" className="mr-2 text-white" />
                Subiendo...
              </>
            ) : (
              <>
                <Upload className="mr-2 size-4" />
                Subir nuevo archivo
              </>
            )}
          </Button>
          <p className="mt-2 text-xs text-gray-500">
            Solo archivos CSV con las columnas: {EXPECTED_COLUMNS.join(", ")}.
          </p>
        </div>
      </div>
    </MainSectionSection>
  );
};

export default PortalCodesContainer;
